/* global Vue, LOGOS, instantsearch, ALGOLIA_APP_ID, ALGOLIA_PUBLIC_API_KEY, ALGOLIA_INDEX, SEARCH_SUGGESTIONS, LOCALE, VueI18n, CLD_URL, CLD_TRANSFORM, AZURE_URI, BOOKING_APP_URI, PREFIX, useOffers  */

import { messagesSpecific } from '../i18n/translations'
import { Notifications } from './components/Notifications'
import { CreateI18n } from '../i18n/create-i18n'
import SearchHeader from './components/SearchHeader'
import AuthService from '../AuthService'

const i18n = CreateI18n(messagesSpecific)

const SearchApp = Vue.createApp({
  name: 'SearchApp',
  components: { Notifications, SearchHeader },
  template: `
<Notifications />
<div>
  <button :title="$t('search')" class="header__icon" @click="openSearch">
    <span class="header__icon--search">
      <i class="icon-search"></i>
    </span>
  </button>
  <div v-if="showSearch" class="search">
    <header class="header header--simple">
      <SearchHeader @close="showSearch = false" />

      <div class="container grid section-padding-small header__search">
        <div class="col-12 col-lg-10 offset-lg-1 col-xl-8 offset-xl-2">
          <label class="search__input">
            <span><i class="icon-search"></i></span>
            <div ref="searchBox"></div>
          </label>
          <template v-if="switchLanguage">
            <div class="search__suggestions">
              <h3></h3>
              <button @click="switchLanguage.action">{{ switchLanguage.text }}</button>
            </div>
          </template>
          <template v-else-if="appliedFiltersCount === 0">
            <h3 class="search__suggestions-title">{{ $t('searchApp.suggestionsTitle') }}</h3>
            <div class="search__suggestions">
              <button v-for="suggestion in suggestions" @click="() => applySuggestion(suggestion.query, searchInstance)">{{ suggestion.title }}</button>
            </div>
          </template>
          <div class="search__refinement">
            <div ref="searchCurrentRefinements"></div>
            <div ref="searchClearRefinements"></div>
          </div>
          <button class="search__toggle-filters button button--outline" @click="openFilters">
            {{ $t('searchApp.showFilters') }} <span v-if="appliedFiltersCount > 0"> ({{ appliedFiltersCount }})</span>
          </button>
        </div>
      </div>
    </header>

    <div class="container grid section-padding-small">
      <div class="search__refinements col-12 col-md-3 offset-lg-1 col-xl-2 offset-xl-2" :class="{ 'open': filtersOpen }">

        <header class="header header--simple">
          <SearchHeader @close="closeFilters" />
        </header>

        <div class="search__refinements__container">
          <h3 class="search__refinements__title">
            <div>
              {{ $t('filters') }}
              <span v-if="appliedFiltersCount > 0"> ({{ appliedFiltersCount }})</span>
            </div>
            <div ref="searchClearRefinementsMobile"></div>
          </h3>
          <div class="title">{{ $t('searchApp.filters.propertyType') }}</div>
          <div ref="propertyTypeRefinement" class="search__refinements__primary"></div>
          <div class="title">{{ $t('searchApp.filters.destination') }}</div>
          <div ref="destinationRefinement"></div>
          <div class="title">{{ $t('searchApp.filters.brand') }}</div>
          <div ref="brandRefinement"></div>
          <!-- Useful for debugging -->
          <div hidden>
            <div class="title">Type</div>
            <div ref="categoryRefinement"></div>
          </div>
        </div>

        <div class="footer">
          <button class="button button--primary" v-if="filtersChangedSinceLastConfirm" @click="filtersOpen = false">{{ $t('searchApp.applyFilters') }}</button>
          <button class="button button--outline" v-else @click="filtersOpen = false">{{ $t('searchApp.cancelFilters') }}</button>
        </div>
      </div>
      <div class="col-12 col-md-9 col-lg-7 col-xl-6">
        <div ref="searchStats"></div>
        <div class="search__hits" ref="searchHits"></div>
        <div class="search__pagination" ref="searchPagination"></div>
      </div>
    </div>
  </div>
</div>
  `,
  setup () {
    const { t } = VueI18n.useI18n()
    const showSearch = Vue.ref(false)
    const allOffers = Vue.ref(null)

    // Containers for instantsearch widgets
    const searchBox = Vue.ref(null)
    const searchHits = Vue.ref(null)
    const searchStats = Vue.ref(null)
    const searchPagination = Vue.ref(null)
    const searchCurrentRefinements = Vue.ref(null)
    const searchClearRefinements = Vue.ref(null)
    const searchClearRefinementsMobile = Vue.ref(null)
    const propertyTypeRefinement = Vue.ref(null)
    const destinationRefinement = Vue.ref(null)
    const brandRefinement = Vue.ref(null)
    const categoryRefinement = Vue.ref(null)

    const availabilityReqestCache = Vue.ref({})

    // Managing filters state - we use the term filters for our own logic while instantsearch uses refinemenets
    const currentSearchState = Vue.ref(null)
    const lastConfirmedSearchState = Vue.ref(null)
    const filtersOpen = Vue.ref(false)
    const filtersChangedSinceLastConfirm = Vue.ref(false)
    const appliedFiltersCount = Vue.computed(() => {
      if (currentSearchState.value?.[ALGOLIA_INDEX]?.refinementList) {
        return Object.values(currentSearchState.value[ALGOLIA_INDEX].refinementList)
          .reduce((acc, item) => acc + item.length, 0)
      }
      return 0
    })

    const searchInstance = Vue.ref(null)
    const suggestions = SEARCH_SUGGESTIONS.filter(s => s.location.includes('search'))
    const switchLanguage = Vue.computed(() => {
      if (
        window.LOCALE !== 'en' &&
        currentSearchState.value?.[ALGOLIA_INDEX].query?.toLowerCase() === 'english' &&
        window.TRANSLATIONS.find((t) => t.lang === 'en')
      ) {
        return {
          text: 'Switch to English',
          action: () => {
            window.location = window.TRANSLATIONS.find((t) => t.lang === 'en').url
          },
        }
      }
      if (
        window.LOCALE !== 'de' &&
        currentSearchState.value?.[ALGOLIA_INDEX].query?.toLowerCase() === 'deutsch' &&
        window.TRANSLATIONS.find((t) => t.lang === 'de')
      ) {
        return {
          text: 'Wechseln Sie zu Deutsch',
          action: () => {
            window.location = window.TRANSLATIONS.find((t) => t.lang === 'de').url
          },
        }
      }
      return null
    })

    function openSearch () {
      showSearch.value = true

      setTimeout(() => {
        initializeInstantSearch()
        searchBox.value.querySelector('input').focus()
      }, 50)
    }

    function openFilters () {
      filtersOpen.value = true
      document.body.classList.add('overflow-hidden')
      lastConfirmedSearchState.value = currentSearchState.value
      filtersChangedSinceLastConfirm.value = false
    }

    function closeFilters () {
      filtersOpen.value = false
      document.body.classList.remove('overflow-hidden')
      searchInstance.value.setUiState(lastConfirmedSearchState.value)
    }

    function kebabToText (string) {
      return string.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    }

    function transformCldUrl (url, width, height) {
      if (!url || !url.startsWith(CLD_URL)) {
        return url
      }

      const widthParam = width ? `w_${width},` : ''
      const heightParam = height ? `h_${height},` : ''
      return url.replace(CLD_URL, `${CLD_URL}${widthParam}${heightParam}${CLD_TRANSFORM}/`)
    }

    function applySuggestion (query, searchInstance) {
      searchInstance.helper.setQuery(query).search()
    }

    async function getUnitAvailabilityCachedAsync (params) {
      if (AuthService.isMaiFamily) {
        params = { ...params, isMaiFamily: true }
      }
      const queryParamsString = Object.keys(params)
        .filter(key => params[key] != null)
        .map(key => `${key}=${params[key]}`)
        .join('&')

      if (availabilityReqestCache.value[queryParamsString] === undefined) {
        availabilityReqestCache.value[queryParamsString] = getUnitAvailabilityAsync(params)
      }

      return availabilityReqestCache.value[queryParamsString]
    }

    async function getUnitAvailabilityAsync (params) {
      const queryParamsString = Object.keys(params)
        .filter(key => params[key] != null)
        .map(key => `${key}=${params[key]}`)
        .join('&')

      try {
        const response = await fetch(`${AZURE_URI}GetAvailabilityCalendar?${queryParamsString}`)
        if (!response.ok) {
          throw new Error(response.statusText)
        }

        return await response.json()
      } catch (e) {
        console.log('Unit availability error', e)
      }
    }

    async function getPropertyAvailabilityCachedAsync (queryParams) {
      const queryParamsString = Object.keys(queryParams)
        .filter(key => queryParams[key] != null)
        .map(key => `${key}=${queryParams[key]}`)
        .join('&')
      if (availabilityReqestCache.value[queryParamsString] === undefined) {
        availabilityReqestCache.value[queryParamsString] = getPropertyAvailabilityAsync(queryParams)
      }
      return availabilityReqestCache.value[queryParamsString]
    }

    async function getPropertyAvailabilityAsync (queryParams) {
      try {
        const response = await fetch(`${AZURE_URI}GetPropertyAvailability?` + Object.keys(queryParams).map(k => `${k}=${queryParams[k]}`).join('&'))
        if (!response.ok) {
          throw new Error(response.statusText)
        }
        return await response.json()
      } catch (e) {
        console.log('Availability error', e)
      }
    }

    function formatISODate (date) {
      return date instanceof Date
        ? date.toISOString().substring(0, 10)
        : new Date(date).toISOString().substring(0, 10)
    }

    function getOfferURL (hit) {
      const offer = allOffers.value.find(o => o.link === `${hit.url}/`)
      if (offer && offer.variations.length === 1 && offer.variations[0].phobsId) {
        return `${BOOKING_APP_URI}${PREFIX}/step1/?propertyId=${offer.variations[0].phobsId}` +
        (offer.variations[0].ratePlanId
          ? `&ratePlanId=${offer.variations[0].ratePlanId}`
          : '')
      }
      return `${hit.url}#/#variations`
    }

    async function getPropertyBookingURL (propertyId, unitId) {
      let startDate = new Date()
      const endDate = new Date()
      endDate.setDate(startDate.getDate() + 1)

      const queryParams = {
        id: propertyId,
        start: formatISODate(startDate),
        end: formatISODate(endDate),
        locale: LOCALE,
      }

      const availability = await getPropertyAvailabilityCachedAsync(queryParams)

      if (availability && (availability.rateFrom || availability.firstAvailableDate)) {
        let currency = availability.currency

        if (unitId) {
          const unitAvailability = await getUnitAvailabilityCachedAsync({
            id: propertyId,
            unitId,
            locale: LOCALE,
          })

          startDate = unitAvailability[0]?.date || formatISODate(startDate)
        } else if (availability.firstAvailableDate) {
          startDate = availability.firstAvailableDate.date
          currency = availability.firstAvailableDate.currency
        } else {
          startDate = formatISODate(startDate)
        }

        return `${BOOKING_APP_URI}${PREFIX}/step1?propertyId=${propertyId}&date=${startDate}&currency=${currency}&unit=${unitId || ''}`
      }

      return null
    }

    const templates = {
      empty: t('searchApp.noResults'),
      item (hit, { html, components }) {
        const image = transformCldUrl(hit.image, 768, 432)
        const brandLogoHtml = hit.type === 'property' ? html`<div class="search__content__brand"><img src="${LOGOS[hit.brand].desktop}" /></div>` : ''

        const imageLabelHtml = hit.type === 'offer' ? html`<div class="search__image__label">${t('searchApp.resultType.' + hit.type)}</div>` : ''

        let starsHtml = ''
        if (hit.type === 'property' && hit.stars) {
          starsHtml = html`
            <div class="card__stars">
              <span class="stars theme--${hit.brand}">${[...Array(hit.stars)].map(() => html`<i class="icon-star"></i>`)}</span>
              <span>${hit.destination[0]}</span>
            </div>
          `
        }

        let tagsHtml = ''
        const properties = hit.property && hit.type !== 'property' ? hit.property : []
        const destinations = hit.destination && hit.type !== 'property' ? hit.destination : []
        const tags = [...properties, ...destinations]
        if (tags.length > 6) {
          const tagsMore = tags.length - 6
          tags.splice(6)
          tags.push(`+${tagsMore}`)
        }

        if (tags.length > 0) {
          tagsHtml = html`
            <div class="search__content__tags">
              ${tags.map((tag) => html`<span>${tag}</span>`)}
            </div>
          `
        }

        let bookNowButton = ''
        if (hit.type === 'offer') {
          bookNowButton = html`
            <a href="${getOfferURL(hit)}" class="button">${t('bookNow')}</a>
          `
        } else if ((hit.type === 'property' || (hit.type === 'property-units' && hit.propertyType.includes('camping'))) && hit.phobsId) {
          bookNowButton = html`
            <a href="" class="button async-book-link is-hidden" aria-hidden="true" data-property="${hit.phobsId}">${t('bookNow')}</a>
          `
        } else if (hit.type === 'unit' && hit.phobsId) {
          bookNowButton = html`
            <a href="" class="button async-book-link is-hidden" aria-hidden="true" data-property="${hit.phobsId}" data-unit="${hit.unitId}">${t('bookNow')}</a>
          `
        }

        return html`
          <div class="search__image">${imageLabelHtml}<img src="${image}" /></div>
          <div class="search__content">
            ${brandLogoHtml}
            <h3><a href="${hit.url}">${components.Highlight({ hit, attribute: 'title' })}</a></h3>
            ${starsHtml}
            <p>${components.Snippet({ hit, attribute: 'description' })}</p>
            ${tagsHtml}
            <div class="search__buttons">
              ${bookNowButton}
              <a href="${hit.url}" class="arrow-button">${t('discoverMore')} <i class="icon-arrow-right-long"></i></a>
            </div>
          </div>
        `
      },
    }

    function initializeInstantSearch () {
      const { liteClient: algoliasearch } = window['algoliasearch/lite']
      const searchClient = algoliasearch(
        ALGOLIA_APP_ID,
        ALGOLIA_PUBLIC_API_KEY,
      )

      const search = instantsearch({
        indexName: ALGOLIA_INDEX,
        searchClient,
        onStateChange ({ uiState, setUiState }) {
          currentSearchState.value = uiState
          filtersChangedSinceLastConfirm.value = true
          setUiState(uiState)
        },
        useCookie: true,
        insights: true,
        // routing: true, // useful for debugging
      })

      const configure = {
        disjunctiveFacetsRefinements: { lang: [LOCALE === 'si' ? 'sl' : LOCALE], searchable: [true] },
      }

      currentSearchState.value = { [ALGOLIA_INDEX]: { configure } }

      search.addWidgets([
        instantsearch.widgets.searchBox({
          container: searchBox.value,
          autofocus: true,
          showSubmit: false,
          showLoadingIndicator: false,
          placeholder: t('searchApp.placeholder'),
          templates: {
            reset: '<i class="icon-close"></i>',
          },
        }),
        instantsearch.widgets.configure(configure),
        instantsearch.widgets.currentRefinements({
          container: searchCurrentRefinements.value,
          excludedAttributes: ['lang', 'query', 'searchable'],
        }),
        instantsearch.widgets.clearRefinements({
          container: searchClearRefinements.value,
          excludedAttributes: ['lang', 'query', 'searchable'],
          templates: {
            resetLabel: t('searchApp.clearFilters'),
          },
        }),
        instantsearch.widgets.clearRefinements({
          container: searchClearRefinementsMobile.value,
          excludedAttributes: ['lang', 'query', 'searchable'],
          templates: {
            resetLabel: t('searchApp.clearFilters'),
          },
        }),
        instantsearch.widgets.stats({
          container: searchStats.value,
          templates: {
            text: ({ nbHits, page, hitsPerPage, query }, { html }) => {
              if (query === '' || nbHits === 0) return ''

              return html`<div class="search__stats">${t('searchApp.showing')} ${page * hitsPerPage + 1} - ${Math.min((page + 1) * hitsPerPage, nbHits)} ${t('searchApp.outOf')} ${nbHits} ${t('searchApp.results')}</div>`
            },
          },
        }),
        instantsearch.widgets.hits({
          container: searchHits.value,
          templates,
        }),
        instantsearch.widgets.refinementList({
          attribute: 'propertyType',
          container: propertyTypeRefinement.value,
          transformItems: items => items.map(item => ({
            ...item,
            highlighted: `${t('propertyTypes.' + item.highlighted)}`,
          })),
        }),
        instantsearch.widgets.refinementList({
          attribute: 'destination',
          container: destinationRefinement.value,
          transformItems: items => items.map(item => ({
            ...item,
            highlighted: kebabToText(item.highlighted),
          })),
        }),
        instantsearch.widgets.refinementList({
          attribute: 'brand',
          container: brandRefinement.value,
          transformItems: items => items.map(item => ({
            ...item,
            highlighted: kebabToText(item.highlighted),
          })),
        }),
        instantsearch.widgets.refinementList({
          attribute: 'category',
          container: categoryRefinement.value,
          sortBy: ['count:desc'],
          transformItems: items => items.map(item => ({
            ...item,
          })),
        }),
        instantsearch.widgets.pagination({
          showFirst: false,
          showLast: false,
          padding: 1,
          scrollTo: searchHits.value,
          container: searchPagination.value,
          templates: {
            previous: `<i class="icon-arrow-left"></i> ${t('previous')}`,
            next: `${t('next')} <i class="icon-arrow-right"></i>`,
          },
        }),
      ])

      searchInstance.value = search
      search.start()

      search.on('render', () => {
        const asyncBookLinks = searchHits.value.querySelectorAll('.async-book-link')

        asyncBookLinks.forEach(async (link) => {
          const { property, unit } = link.dataset

          const bookingUrl = await getPropertyBookingURL(property, unit)

          if (bookingUrl) {
            link.classList.remove('is-hidden')
            link.setAttribute('aria-hidden', 'false')
            link.href = bookingUrl
          }
        })
      })
    }

    Vue.watch(showSearch, (show) => {
      if (show) {
        document.body.classList.add('overflow-hidden')
      } else {
        document.body.classList.remove('overflow-hidden')
      }
    })

    Vue.onMounted(async () => {
      // openSearch()
      allOffers.value = await useOffers()
    })

    return {
      searchInstance,
      applySuggestion,
      searchBox,
      searchHits,
      searchStats,
      searchPagination,
      searchCurrentRefinements,
      searchClearRefinements,
      searchClearRefinementsMobile,
      propertyTypeRefinement,
      destinationRefinement,
      brandRefinement,
      categoryRefinement,
      showSearch,
      openSearch,
      LOGOS,
      ALGOLIA_INDEX,
      filtersOpen,
      openFilters,
      closeFilters,
      appliedFiltersCount,
      filtersChangedSinceLastConfirm,
      suggestions,
      switchLanguage,
    }
  },
})

const appElement = document.getElementById('search-app')

if (appElement) {
  SearchApp.use(i18n)
  SearchApp.mount(appElement)
}

export default SearchApp
